// Admin webhook-log API surface — the read-only viewer over `payment_events`.
// M7f (docs/design-notes/m7f-webhook-log.md).
//
// Two calls on purpose. `raw_payload` is arbitrary jsonb with no size ceiling,
// so the list endpoint never sends it and this client never asks for it: the
// heavy column crosses the wire only when a drawer opens.
//
// There is deliberately no `replayWebhookEvent` — see the route file for why.

import { api } from './api';

export interface WebhookEventRow {
  id: string;
  provider: string;
  /** The idempotency key. NULLABLE — the mock provider writes null. */
  providerEventId: string | null;
  eventKind: string;
  subscriptionId: string | null;
  receivedAt: string;
  /** ≤3 allow-listed scalar keys lifted from the payload, already redacted. */
  summary: Record<string, string>;
}

export interface WebhookEventList {
  rows: WebhookEventRow[];
  total: number;
  limit: number;
  offset: number;
  kinds: string[];
  providers: string[];
}

export interface WebhookEventDetail extends WebhookEventRow {
  /** REDACTED server-side. There is no unredacted view, by design. */
  payload: Record<string, unknown>;
  truncated: boolean;
  payloadKeys: string[];
}

export interface WebhookEventFilters {
  q?: string;
  kind?: string;
  provider?: string;
  subscriptionId?: string;
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}

export async function listWebhookEvents(
  filters: WebhookEventFilters,
  signal?: AbortSignal,
): Promise<WebhookEventList> {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  const { data } = await api<{ data: WebhookEventList }>(`/admin/webhooks${qs ? `?${qs}` : ''}`, {
    signal,
  });
  return data;
}

export async function getWebhookEvent(
  id: string,
  signal?: AbortSignal,
): Promise<WebhookEventDetail> {
  const { data } = await api<{ data: WebhookEventDetail }>(
    `/admin/webhooks/${encodeURIComponent(id)}`,
    { signal },
  );
  return data;
}

/**
 * Flatten a nested payload to dot-paths (`card.last4`, `items.0.amount`) so the
 * drawer can render each as a DetailRow — the audit viewer's flat
 * `Object.entries(metadata)` loop extended by one step rather than replaced.
 * Arrays and objects that bottom out empty keep a placeholder so their presence
 * is still visible.
 */
export function flattenPayload(
  value: unknown,
  prefix = '',
  depth = 0,
  out: Record<string, string> = {},
): Record<string, string> {
  const MAX_DEPTH = 6;
  const MAX_KEYS = 80;
  if (Object.keys(out).length >= MAX_KEYS) return out;

  if (value === null || value === undefined) {
    out[prefix || '(root)'] = '—';
    return out;
  }
  if (typeof value !== 'object') {
    // Narrowed to a primitive by the check above.
    // eslint-disable-next-line @typescript-eslint/no-base-to-string
    out[prefix || '(root)'] = String(value);
    return out;
  }
  if (depth >= MAX_DEPTH) {
    out[prefix || '(root)'] = JSON.stringify(value);
    return out;
  }

  const entries = Array.isArray(value)
    ? value.map((v, i) => [String(i), v] as const)
    : Object.entries(value as Record<string, unknown>);

  if (entries.length === 0) {
    out[prefix || '(root)'] = Array.isArray(value) ? '[]' : '{}';
    return out;
  }
  for (const [key, child] of entries) {
    flattenPayload(child, prefix ? `${prefix}.${key}` : key, depth + 1, out);
  }
  return out;
}
